import { useCallback, useEffect, useState } from "react";
import { Gift, TrendingUp, ShoppingCart, Percent, Loader2 } from "lucide-react";
import api, { formatMoney } from "../lib/apiClient";

const RANGES = [
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
  { id: 36500, label: "All time" },
];

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />
        <span className="eyebrow">{label}</span>
      </div>
      <p className="font-display font-extrabold text-2xl mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export function FreeGiftReport() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/promo/free-gift/report", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="card-soft rounded-2xl p-6 mb-8" data-testid="free-gift-report-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#f4e7c4] text-[#8a681c] flex items-center justify-center shrink-0"><Gift className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display font-bold text-lg">Free Gift — Redemption Report</h3>
            <p className="text-sm text-muted-foreground mt-0.5">How often the gift was granted on paid orders and its lift on average order value.</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {RANGES.map((r) => (
            <button key={r.id} onClick={() => setDays(r.id)} data-testid={`free-gift-report-range-${r.id}`}
              className={`pill px-3 py-1.5 text-xs font-semibold border transition-colors ${days === r.id ? "btn-ink border-transparent" : "bg-white text-muted-foreground border-border hover:text-foreground"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground mt-5 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading report…</p>
      ) : !data ? (
        <p className="text-sm text-muted-foreground mt-5" data-testid="free-gift-report-empty">Couldn't load the report. Try again.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            <Stat testid="fg-report-redemptions" icon={Gift} label="Gifts redeemed" value={data.redemptions}
              sub={`${formatMoney(data.gift_order_revenue)} order revenue`} />
            <Stat testid="fg-report-rate" icon={Percent} label="Redemption rate" value={`${data.redemption_rate}%`}
              sub={`of ${data.paid_orders} paid orders`} />
            <Stat testid="fg-report-aov-lift" icon={TrendingUp} label="AOV lift"
              value={data.redemptions ? `${data.aov_lift >= 0 ? "+" : ""}${formatMoney(data.aov_lift)}` : "—"}
              sub={data.redemptions ? `${data.aov_lift_pct >= 0 ? "+" : ""}${data.aov_lift_pct}% vs non-gift` : "no gift orders yet"} />
            <Stat testid="fg-report-aov-with" icon={ShoppingCart} label="AOV with gift"
              value={data.redemptions ? formatMoney(data.aov_with_gift) : "—"}
              sub={`${formatMoney(data.aov_without_gift)} without`} />
          </div>
          {data.unpaid_granted > 0 && (
            <p className="text-xs text-muted-foreground mt-4" data-testid="fg-report-unpaid">
              {data.unpaid_granted} qualifying {data.unpaid_granted === 1 ? "cart" : "carts"} received the gift but didn't complete checkout.
            </p>
          )}
        </>
      )}
    </div>
  );
}
